import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import type { 
  AITextGenerationRequest, 
  AITextGenerationResponse, 
  UseAITextGenerationOptions,
  UseAITextGenerationReturn 
} from './types';

export function useAITextGeneration(options: UseAITextGenerationOptions = {}): UseAITextGenerationReturn {
  const { executeAIOperation, isLoading, error } = useAIOperationWrapper();

  const generateText = async (request: Omit<AITextGenerationRequest, 'type'>): Promise<string> => {
    return executeAIOperation(
      async () => {
        const { data, error: functionError } = await supabase.functions.invoke('threaded-text-generation', {
          body: {
            ...request,
            type: 'ai-text-generation',
            appName: request.appName || 'Content Generator',
            functionName: request.functionName || 'Text Enhancement'
          }
        });

        if (functionError) {
          throw new Error(functionError.message || 'Failed to generate text');
        }

        if (!data?.content) {
          throw new Error('No content received from AI generation');
        }

        // Clean code fences immediately at the hook level
        const rawContent = data.content;
        const cleanContent = rawContent
          .replace(/^```html\s*\n?/gm, '')
          .replace(/^```\s*\n?/gm, '')
          .replace(/\n?```$/gm, '')
          .trim();

        // Log the cleaning process for debugging
        if (rawContent !== cleanContent) {
          console.log('🧹 Hook-level content cleaning:', {
            raw: rawContent.substring(0, 50) + '...',
            cleaned: cleanContent.substring(0, 50) + '...',
            hadCodeFences: rawContent.includes('```')
          });
        }

        return cleanContent;
      },
      {
        immediate: true, // AI text generation needs immediate feedback
        errorPrefix: 'AI text generation failed',
        debug: false
      }
    );
  };

  return {
    generateText,
    isLoading,
    error: error ? new Error(error) : null,
  };
}
